import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Globe,
  FileText,
  Palette,
  Save,
  Eye,
  Sparkles
} from 'lucide-react'

const NewSite = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedTemplate, setSelectedTemplate] = useState('blank')

  const { register, handleSubmit, watch, formState: { errors } } = useForm()

  const siteName = watch('name', '')

  const createSiteMutation = useMutation(
    (siteData) => apiService.post('/sites', siteData),
    {
      onSuccess: (response) => {
        console.log('Site creation successful:', response.data)
        toast.success('Site created successfully!')
        queryClient.invalidateQueries('client-sites')
        queryClient.invalidateQueries('sites')

        setTimeout(() => {
          queryClient.refetchQueries('client-sites')
        }, 100)

        navigate('/client/sites')
      },
      onError: (error) => {
        console.error('Site creation error details:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
          fullError: error
        })

        if (error.response?.status === 201 && error.response?.data?.id) {
          console.log('False error - site was actually created successfully')
          toast.success('Site created successfully!')
          queryClient.invalidateQueries('client-sites')
          queryClient.invalidateQueries('sites')

          setTimeout(() => {
            queryClient.refetchQueries('client-sites')
          }, 100)

          navigate('/client/sites')
          return
        }

        const errorMessage = error.response?.data?.error ||
                           error.response?.data?.errors?.[0]?.msg ||
                           'Failed to create site'
        toast.error(errorMessage)
      }
    }
  )

  const onSubmit = (data) => {
    const siteData = {
      name: data.name,
      template_id: null,
      description: data.description || ''
    }

    console.log('Submitting site creation with data:', siteData)
    createSiteMutation.mutate(siteData)
  }

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  }

  const templates = [
    {
      id: 'blank',
      name: 'Blank Site',
      description: 'Start with a clean slate',
      preview: '/api/templates/blank/preview.jpg',
      category: 'Basic'
    },
    {
      id: 'business',
      name: 'Business Website',
      description: 'Professional business template',
      preview: '/api/templates/business/preview.jpg',
      category: 'Business'
    },
    {
      id: 'portfolio',
      name: 'Portfolio',
      description: 'Showcase your work',
      preview: '/api/templates/portfolio/preview.jpg',
      category: 'Creative'
    },
    {
      id: 'landing',
      name: 'Landing Page',
      description: 'Single page marketing site',
      preview: '/api/templates/landing/preview.jpg',
      category: 'Marketing'
    }
  ]

  return (
    <div className="fade-in" style={{ paddingTop: '1rem', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center gap-4">
          <button
            className="btn btn-ghost btn-icon btn-sm"
            onClick={() => navigate('/client/sites')}
          >
            <ArrowLeft style={{ width: '1.25rem', height: '1.25rem' }} />
          </button>
          <div>
            <h1 className="font-bold" style={{ fontSize: '1.875rem', color: 'var(--text)', margin: 0 }}>
              Create New Site
            </h1>
            <p className="text-soft" style={{ marginTop: '0.25rem' }}>
              Set up a new website for your business
            </p>
          </div>
        </div>
      </motion.div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '2rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: '2rem', alignItems: 'start' }}>
          {/* Site Configuration */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
          >
            <div className="card card-pad">
              <div className="flex items-center gap-2" style={{ marginBottom: '1rem' }}>
                <div style={{ padding: '0.5rem', background: 'var(--accent-soft)', borderRadius: 'var(--radius)' }}>
                  <Globe style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)' }} />
                </div>
                <div>
                  <h2 className="font-semibold" style={{ fontSize: '1rem', color: 'var(--text)', margin: 0 }}>Site Information</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem' }}>Configure the basic details for your new website</p>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div>
                  <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                    Site Name <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <input
                    type="text"
                    {...register('name', { required: 'Site name is required' })}
                    className="input w-full"
                    style={errors.name ? { borderColor: 'var(--danger)' } : {}}
                    placeholder="Enter site name"
                  />
                  {errors.name && (
                    <p style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem' }}>{errors.name.message}</p>
                  )}
                  {siteName && (
                    <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.5rem', background: 'var(--surface-2)', padding: '0.5rem 0.75rem', borderRadius: 'var(--radius-sm)' }}>
                      <strong>URL slug:</strong> {generateSlug(siteName)}
                    </p>
                  )}
                </div>

                <div>
                  <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                    Description
                  </label>
                  <textarea
                    {...register('description')}
                    rows={4}
                    className="input w-full"
                    style={{ resize: 'none' }}
                    placeholder="Optional description for this site"
                  />
                </div>
              </form>
            </div>

            {/* Template Selection */}
            <div className="card card-pad">
              <div className="flex items-center gap-2" style={{ marginBottom: '1rem' }}>
                <div style={{ padding: '0.5rem', background: 'var(--accent-soft)', borderRadius: 'var(--radius)' }}>
                  <Palette style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)' }} />
                </div>
                <div>
                  <h2 className="font-semibold" style={{ fontSize: '1rem', color: 'var(--text)', margin: 0 }}>Choose Template</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem' }}>Select a starting template for your website</p>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                {templates.map((template) => (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => setSelectedTemplate(template.id)}
                    style={{
                      position: 'relative',
                      cursor: 'pointer',
                      borderRadius: 'var(--radius)',
                      border: selectedTemplate === template.id ? '2px solid var(--accent)' : '2px solid var(--border)',
                      padding: '1rem',
                      background: selectedTemplate === template.id ? 'var(--accent-soft)' : 'var(--surface)',
                      transition: 'all 0.2s',
                    }}
                  >
                    <div style={{ aspectRatio: '16/9', background: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FileText style={{ width: '2rem', height: '2rem', color: 'var(--text-faint)' }} />
                    </div>
                    <h3 className="font-semibold" style={{ color: 'var(--text)', marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                      {template.name}
                    </h3>
                    <p className="text-soft" style={{ fontSize: '0.875rem' }}>
                      {template.description}
                    </p>
                    <div style={{ marginTop: '0.5rem' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0.25rem 0.5rem', borderRadius: 'var(--radius-sm)', fontSize: '0.75rem', fontWeight: 500, background: 'var(--surface-2)', color: 'var(--text-soft)' }}>
                        {template.category}
                      </span>
                    </div>
                    {selectedTemplate === template.id && (
                      <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', width: '1.5rem', height: '1.5rem', background: 'var(--accent)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: 'white', fontSize: '0.75rem' }}>✓</span>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Preview and Actions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="card card-pad" style={{ position: 'sticky', top: '1.5rem' }}>
              <div className="flex items-center gap-2" style={{ marginBottom: '1rem' }}>
                <div style={{ padding: '0.5rem', background: 'var(--success-soft)', borderRadius: 'var(--radius)' }}>
                  <Eye style={{ width: '1.25rem', height: '1.25rem', color: 'var(--success)' }} />
                </div>
                <div>
                  <h2 className="font-semibold" style={{ fontSize: '1rem', color: 'var(--text)', margin: 0 }}>Preview &amp; Create</h2>
                  <p className="text-muted" style={{ fontSize: '0.875rem' }}>Review and create your website</p>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Template Preview */}
                <div>
                  <h4 className="font-semibold text-soft" style={{ fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                    Selected Template
                  </h4>
                  <div style={{ aspectRatio: '16/9', background: 'var(--surface-2)', borderRadius: 'var(--radius)', border: '2px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                      <FileText style={{ width: '3rem', height: '3rem', color: 'var(--text-faint)', margin: '0 auto 0.5rem' }} />
                      <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                        {templates.find(t => t.id === selectedTemplate)?.name}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Site Summary */}
                {siteName && (
                  <div style={{ padding: '1rem', background: 'var(--accent-soft)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--accent-3)' }}>
                    <h4 className="font-semibold" style={{ fontSize: '0.875rem', color: 'var(--accent)', marginBottom: '0.75rem' }}>
                      Site Summary
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
                      <div className="flex justify-between">
                        <span className="text-soft">Name:</span>
                        <span className="font-medium" style={{ color: 'var(--text)' }}>{siteName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-soft">Slug:</span>
                        <span className="font-medium" style={{ color: 'var(--text)', fontFamily: 'monospace' }}>/{generateSlug(siteName)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-soft">Template:</span>
                        <span className="font-medium" style={{ color: 'var(--text)' }}>
                          {templates.find(t => t.id === selectedTemplate)?.name}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
                  <button
                    className="btn btn-primary w-full"
                    onClick={handleSubmit(onSubmit)}
                    disabled={createSiteMutation.isLoading}
                    style={{ height: '2.75rem', fontSize: '1rem' }}
                  >
                    {createSiteMutation.isLoading ? (
                      <>
                        <div style={{ width: '1rem', height: '1rem', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        Creating Site...
                      </>
                    ) : (
                      <>
                        <Save style={{ width: '1rem', height: '1rem' }} />
                        Create Site
                      </>
                    )}
                  </button>

                  <button
                    className="btn btn-ghost w-full"
                    onClick={() => navigate('/client/sites')}
                    style={{ height: '2.75rem', fontSize: '1rem' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default NewSite
